<script setup>
import { ref } from "vue";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { storeToRefs } from "pinia";

gsap.registerPlugin(ScrollToPlugin);

const entriesStore = useEntriesStore();

const {
  current,
  title,
  seoTitle,
  seoDescription,
  seoImage,
  blocks,
  width,
  bgColor,
  bgGradient,
  textColor,
  fullPath,
} = storeToRefs(entriesStore);

const globalStore = useGlobalStore();
const { mounted, canTouch, scrolling, mouseDown } = storeToRefs(globalStore);
const config = useRuntimeConfig();
const constants = useConstants();
const route = useRoute();

const response = await $fetch("/api/work/" + route?.params?.slug)
  .then(function (response) {
    entriesStore.setCurrent(response);
  })
  .catch(function (error) {
    throw createError({
      statusCode: 404,
      statusMessage: "Page Not Found",
    });
  });

let scrollTimeout = null;

const data = reactive({
  topPosition: 0,
  newTop: 0,
  height: Number,
  contentPosition: String,
  currentEl: Object,
  currentElRect: Object,
  top: Number,
  left: Number,
  h: Number,
  w: Number,
  scrollTop: Number,
});

definePageMeta({
  layout: "default",
});
useHead({
  title: "Id",
  link: [
    {
      rel: "canonical",
      href: fullPath,
    },
  ],
});
useSeoMeta({
  title: (seoTitle?.value ?? title?.value ?? "") + constants.app.title,
  description: seoDescription?.value ?? null,
  image: seoImage?.value ?? null,
  ogSiteName: constants.app.siteName,
  ogType: "website",
  ogTitle: (seoTitle?.value ?? title?.valuee ?? "") + constants.app.title,
  ogDescription: seoDescription?.value ?? null,
  ogImage: seoImage?.value ?? null,
  ogUrl: fullPath,
  ogArticlePublisher: constants.app.social.facebook,
  twitterTitle: (seoTitle?.value ?? title?.value ?? "") + constants.app.title,
  twitterDescription: seoDescription?.value ?? null,
  twitterImage: seoImage?.value ?? null,
});

const isMounted = ref(false);
const computedBackgroundColor = computed(function () {
  return bgColor?.value;
});
const computedBackgroundGradient = computed(function () {
  return bgGradient?.value;
});
const computedTop = computed(function () {
  if (!isMounted.value) {
    return null;
  } else {
    setupPosition();
    return { top: data.top + data.h - 110 + "px" };
  }
});

function setupPosition() {
  data.w = document.body.offsetWidth;
  data.currentElRect = data.currentEl.getBoundingClientRect();
  data.top = data.currentElRect.top + data.scrollTop;
  data.left = data.currentElRect.left;
  data.h = data.currentEl.offsetHeight;

  if (data.left < data.w / 2) {
    data.contentPosition = "left";
  } else {
    data.contentPosition = "right";
  }

  const workEl = document.getElementById("work");
  if (workEl) {
    setTimeout(() => {
      const workRect = workEl.getBoundingClientRect();
      const workBottom = workRect.bottom + window.scrollY;
      document.body.style.minHeight = `${workBottom}px`;
    }, 0);
  }
}

function eventListeners() {
  window.addEventListener("resize", function () {
    reposition();
  });

  window.addEventListener("scroll", handleScroll);

  window.addEventListener("mousedown", () => globalStore.setMouseDown(), true);

  window.addEventListener("mouseup", function () {
    globalStore.setMouseUp(), true;
    handleScroll();
  });
  window.addEventListener("touchstart", function () {
    globalStore.hasTouch();
    globalStore.touchStart();
  });
  window.addEventListener("touchend", function () {
    globalStore.touchEnd();
  });
}

const handleScroll = () => {
  globalStore.scrollingStarted();

  if (scrollTimeout) clearTimeout(scrollTimeout);

  scrollTimeout = setTimeout(() => {
    globalStore.scrollingEnded();
  }, 100);
};

watch(scrolling, (newValue) => {
  if (!newValue && !mouseDown.value) {
    onScrollEnd();
  }
});

function reposition() {
  data.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  data.currentEl = document.getElementsByClassName("tile--active")[0];
  setupPosition();
}

async function close() {
  document.body.style.minHeight = "";
  await navigateTo("/");
  entriesStore.setCurrent(null);
}

function getElementY(query) {
  return (
    window.pageYOffset +
    document.querySelector("#" + query).getBoundingClientRect().top -
    90
  );
}
function scrollToY(y, duration) {
  if (canTouch.value) {
    gsap.to(window, 0, {
      duration: duration,
      scrollTo: { y, autoKill: false },
    });
  } else {
    gsap.to(window, {
      duration: duration,
      scrollTo: y,
      ease: "power2",
    });
  }
}

function getElementHeight(id) {
  const ell = document.getElementById(id);
  return document.getElementById(id).clientHeight;
}

function onScrollEnd() {
  const viewportHeight = window.innerHeight;
  const elementHeight = getElementHeight("work");
  // const tileHeight = getElementHeight(route.params.slug);
  const startingY = window.pageYOffset;
  const endingY = window.pageYOffset + viewportHeight;
  const elementYStart = getElementY(route.params.slug);
  const elementYEnd = getElementY("work") + elementHeight;
  const newElementYEnd = elementYEnd - viewportHeight + 120;

  if (startingY < elementYStart) {
    scrollToY(elementYStart, isMounted.value ? 0.1 : 0);
  }

  if (viewportHeight < elementHeight + data.h) {
    // element cannot be fully visible in viewport
    if (endingY > elementYEnd) {
      scrollToY(newElementYEnd, 0.1);
    }
    // }
  } else if (endingY > elementYStart + viewportHeight) {
    scrollToY(elementYStart, 0.1);
  }
}

onMounted(() => {
  data.scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  data.currentEl = document.getElementsByClassName("tile--active")[0];
  isMounted.value = true;

  setupPosition();
  eventListeners();

  scrollToY(getElementY(route.params.slug), isMounted.value ? 0.3 : 0);

  globalStore.isMounted();
});
</script>
<template>
  <div
    v-if="current"
    class="project-detail absolute inset-0 z-2"
    :style="{ visibility: isMounted ? 'visible' : 'hidden' }"
  >
    <div class="l-container relative">
      <div class="flex flex-wrap relative">
        <div
          class="fixed inset-0 bg-black opacity-50 blur-sm z-0"
          @click="close"
        ></div>
        <div
          class="relative w-full mt-md lg:mt-0 z-1"
          :class="[
            width === '50' ? 'md:w-1/2 project-detail__col-50' : '',
            data.contentPosition === 'right' && width === '50'
              ? 'md:left-1/2 md:ml-xs'
              : '',
          ]"
          :style="computedTop"
        >
          <div
            class="details-outline absolute inset-[-10px] bg-white z-0"
          ></div>
          <div id="work">
            <div
              class="relative z-1"
              :style="
                'background-color: ' +
                computedBackgroundColor +
                '; background: ' +
                computedBackgroundGradient
              "
            >
              <div
                :class="{
                  'mb-xl':
                    (current.showTitle && current.title) ||
                    current.subtitle ||
                    current.text,
                }"
              >
                <div
                  class="prose px-md lg:px-lg pt-lg xl:px-xl xl:pt-xl prose--max-w"
                  :class="{ 'prose--black': textColor === 'dark' }"
                >
                  <h1
                    v-if="current.showTitle === true"
                    class="h1--small !mb-lg last:!mb-0"
                  >
                    {{ current.title }}
                  </h1>
                  <h2 v-if="current.subtitle" class="h4 !mb-lg last:!mb-0">
                    {{ current.subtitle }}
                  </h2>
                  <div
                    v-if="current.text"
                    v-html="current.text"
                    class="mb-lg last:mb-0"
                  ></div>
                </div>
              </div>
              <ProjectContent
                v-if="blocks && blocks.length > 0"
                :blocks="blocks"
                :textColor="textColor"
              />
            </div>
            <button
              @click="close"
              class="relative w-full flex justify-center items-center bg-black text-white p-md z-1 border-t border-t-white border-t-20px mt-md"
            >
              <span class="mr-sm">Sluiten</span>
              <span class=""><IconClose /></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.project-detail {
  .prose {
    h1 {
      @apply !text-4xl lg:!text-5xl xl:!text-6xl;
    }
  }

  &__col-50 {
    @media (min-width: 768px) {
      width: calc(50% - 10px);
    }
  }
}

.details-outline {
  width: calc(100% + 40px);
  height: calc(100% + 40px);
  z-index: 0;
  position: absolute;
  left: -20px;
  top: -20px;
}
</style>
